import { defineMessage } from 'react-intl'

import { INFO_OVERLAY_TARGET_FNS, OPTIONS_TYPE, PreferenceMetadataConfig } from './types'
import { MIXPANEL_BUTTON_TEXT } from '../../common/constants/mixpanelConstants'
import { CountryNameToISOCode } from '../../intl/constants'
import {
  MEMBER_PREFERENCE_IDS,
  MEMBER_PREFERENCE_NAMES,
  S4C_PROGRAM_OPTION_FLOW,
} from '../../memberPreferences/constants'
import { MEMBER_PREFERENCE_LABELS } from '../../searchForCare/messages'

const optionsProgressive = [
  {
    text: MEMBER_PREFERENCE_LABELS.man,
    id: MEMBER_PREFERENCE_IDS.MAN,
  },
  {
    text: MEMBER_PREFERENCE_LABELS.woman,
    id: MEMBER_PREFERENCE_IDS.WOMAN,
  },
  {
    text: MEMBER_PREFERENCE_LABELS.nonBinaryPerson,
    id: MEMBER_PREFERENCE_IDS.NON_BINARY_PERSON,
  },
]

const optionsConservative = [
  {
    text: MEMBER_PREFERENCE_LABELS.man,
    id: MEMBER_PREFERENCE_IDS.MAN,
  },
  {
    text: MEMBER_PREFERENCE_LABELS.woman,
    id: MEMBER_PREFERENCE_IDS.WOMAN,
  },
]

const footerTextProgressive = {
  message: defineMessage({
    defaultMessage: 'How Lyra defines non-binary',
    description: 'Text indicating a prompt to define the term non-binary',
  }),
  color: 'textInactive',
  infoOverlay: {
    onPress: {
      fn: INFO_OVERLAY_TARGET_FNS.PRESS_GENDER_PREF_DISCLAIMER,
    },
    content: defineMessage({
      defaultMessage:
        'Lyra celebrates and acknowledges all gender identities and gender expressions, while recognizing that non-binary can mean different things to different people. We define non-binary as not identifying exclusively as a woman or a man. That can mean identifying as one or more of the following: woman, man, non-binary, agender, two-spirit, gender non-conforming, and genderqueer. If you have questions or concerns, <link>contact a Care Navigator.</link>',
      description: 'An explanation of how Lyra defines the term non-binary',
    }),
    linkConfig: {
      onPress: {
        fn: INFO_OVERLAY_TARGET_FNS.PRESS_CARE_TEAM_LINK,
        params: {
          button_text: MIXPANEL_BUTTON_TEXT.CONTACT_A_CARE_NAVIGATOR,
          mixpanelEntryPoint: 'gender identity',
        },
      },
    },
  },
}

const countryConfigProgressive = {
  programOptionFlows: [S4C_PROGRAM_OPTION_FLOW.THERAPY],
  options: optionsProgressive,
  footerText: footerTextProgressive,
}

const countryConfigConservative = {
  programOptionFlows: [S4C_PROGRAM_OPTION_FLOW.THERAPY],
  options: optionsConservative,
}

const genderPreference: PreferenceMetadataConfig = {
  name: MEMBER_PREFERENCE_NAMES.GENDER,
  title: MEMBER_PREFERENCE_LABELS.gender,
  optionType: OPTIONS_TYPE.CHECKBOX,
  icon: 'MemberPreferencesGenderIllustrationIcon',
  promptText: defineMessage({
    defaultMessage: 'I prefer a provider who identifies as any of the following:',
    description: 'Text prompting the user to select options they associate with',
  }),
  countryConfig: {
    [CountryNameToISOCode.Argentina]: countryConfigConservative,
    [CountryNameToISOCode.Australia]: countryConfigProgressive,
    [CountryNameToISOCode.Austria]: countryConfigConservative,
    [CountryNameToISOCode.Belgium]: countryConfigConservative,
    [CountryNameToISOCode.Brazil]: countryConfigProgressive,
    [CountryNameToISOCode.Canada]: countryConfigProgressive,
    [CountryNameToISOCode.Chile]: countryConfigConservative,
    [CountryNameToISOCode.Colombia]: countryConfigConservative,
    [CountryNameToISOCode.Croatia]: countryConfigConservative,
    [CountryNameToISOCode.CzechRepublic]: countryConfigConservative,
    [CountryNameToISOCode.Denmark]: countryConfigConservative,
    [CountryNameToISOCode.Egypt]: countryConfigConservative,
    [CountryNameToISOCode.Finland]: countryConfigConservative,
    [CountryNameToISOCode.France]: countryConfigConservative,
    [CountryNameToISOCode.Germany]: countryConfigConservative,
    [CountryNameToISOCode.Ghana]: countryConfigConservative,
    [CountryNameToISOCode.Greece]: countryConfigConservative,
    [CountryNameToISOCode.HongKong]: countryConfigConservative,
    [CountryNameToISOCode.Hungary]: countryConfigConservative,
    [CountryNameToISOCode.Iceland]: countryConfigConservative,
    [CountryNameToISOCode.India]: countryConfigConservative,
    [CountryNameToISOCode.Indonesia]: countryConfigConservative,
    [CountryNameToISOCode.Ireland]: countryConfigProgressive,
    [CountryNameToISOCode.Israel]: countryConfigProgressive,
    [CountryNameToISOCode.Italy]: countryConfigConservative,
    [CountryNameToISOCode.Japan]: countryConfigConservative,
    [CountryNameToISOCode.Kenya]: countryConfigConservative,
    [CountryNameToISOCode.Lithuania]: countryConfigConservative,
    [CountryNameToISOCode.Malaysia]: countryConfigConservative,
    [CountryNameToISOCode.Mexico]: countryConfigConservative,
    [CountryNameToISOCode.Netherlands]: countryConfigProgressive,
    [CountryNameToISOCode.NewZealand]: countryConfigProgressive,
    [CountryNameToISOCode.Nigeria]: countryConfigConservative,
    [CountryNameToISOCode.Norway]: countryConfigConservative,
    [CountryNameToISOCode.Peru]: countryConfigConservative,
    [CountryNameToISOCode.Philippines]: countryConfigConservative,
    [CountryNameToISOCode.Poland]: countryConfigConservative,
    [CountryNameToISOCode.Portugal]: countryConfigConservative,
    [CountryNameToISOCode.Qatar]: countryConfigConservative,
    [CountryNameToISOCode.RepublicOfKorea]: countryConfigConservative,
    [CountryNameToISOCode.Romania]: countryConfigConservative,
    [CountryNameToISOCode.SaudiArabia]: countryConfigConservative,
    [CountryNameToISOCode.Singapore]: countryConfigConservative,
    [CountryNameToISOCode.Slovakia]: countryConfigConservative,
    [CountryNameToISOCode.SouthAfrica]: countryConfigProgressive,
    [CountryNameToISOCode.Spain]: countryConfigConservative,
    [CountryNameToISOCode.Sweden]: countryConfigConservative,
    [CountryNameToISOCode.Switzerland]: countryConfigConservative,
    [CountryNameToISOCode.Taiwan]: countryConfigProgressive,
    [CountryNameToISOCode.Thailand]: countryConfigConservative,
    [CountryNameToISOCode.Türkiye]: countryConfigConservative,
    [CountryNameToISOCode.Ukraine]: countryConfigConservative,
    [CountryNameToISOCode.UnitedArabEmirates]: countryConfigConservative,
    [CountryNameToISOCode.UnitedKingdom]: countryConfigProgressive,
    [CountryNameToISOCode.UnitedStatesOfAmerica]: {
      ...countryConfigProgressive,
      programOptionFlows: [
        S4C_PROGRAM_OPTION_FLOW.COACHING,
        S4C_PROGRAM_OPTION_FLOW.COACHING_WITH_LMS,
        S4C_PROGRAM_OPTION_FLOW.THERAPY,
      ],
    },
    [CountryNameToISOCode.VietNam]: countryConfigConservative,
  },
}

export default genderPreference
